import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { TrendingUp, DollarSign } from 'lucide-react';

interface EarningsCalculatorProps {
  onCTA?: () => void;
  className?: string;
}

const EarningsCalculator: React.FC<EarningsCalculatorProps> = ({
  onCTA,
  className = ''
}) => {
  const [hoursPerWeek, setHoursPerWeek] = useState(20);
  const [ratePerHour, setRatePerHour] = useState(80);
  
  const sessionsPerWeek = hoursPerWeek;
  const weeklyEarnings = sessionsPerWeek * ratePerHour * 0.9; // 90% after 10% commission
  const monthlyEarnings = weeklyEarnings * 4.33;
  const yearlyEarnings = monthlyEarnings * 12;
  
  return (
    <Card className={`shadow-xl border-2 border-primary/20 ${className}`}>
      <CardHeader className="bg-gradient-to-br from-primary/10 to-accent/10">
        <CardTitle className="text-2xl flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-primary" />
          Calculate Your Potential Earnings
        </CardTitle>
        <CardDescription className="text-base">
          See how much you could earn on our platform
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-8 pt-6">
        {/* Hours Per Week */}
        <div className="space-y-3">
          <div className="flex justify-between">
            <Label htmlFor="hours" className="text-base font-semibold">
              Hours per week
            </Label>
            <span className="text-lg font-bold text-primary">{hoursPerWeek} hrs</span>
          </div>
          <Slider
            id="hours"
            min={5}
            max={40}
            step={5}
            value={[hoursPerWeek]}
            onValueChange={(value) => setHoursPerWeek(value[0])}
            className="w-full"
          />
        </div>
        
        {/* Rate Per Hour */}
        <div className="space-y-3">
          <div className="flex justify-between">
            <Label htmlFor="rate" className="text-base font-semibold">
              Rate per session
            </Label>
            <span className="text-lg font-bold text-primary">${ratePerHour}</span>
          </div>
          <Slider
            id="rate"
            min={30}
            max={200}
            step={10}
            value={[ratePerHour]}
            onValueChange={(value) => setRatePerHour(value[0])}
            className="w-full"
          />
        </div>
        
        {/* Results */}
        <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg p-6 space-y-4">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Your estimated monthly earnings</p>
            <div className="flex items-center justify-center gap-2">
              <DollarSign className="w-8 h-8 text-primary" />
              <span className="text-4xl md:text-5xl font-bold text-foreground">
                ${monthlyEarnings.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              (after 10% platform fee)
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-border">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Weekly</p>
              <p className="text-xl font-bold text-foreground">
                ${weeklyEarnings.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </p>
            </div>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">Yearly</p>
              <p className="text-xl font-bold text-foreground">
                ${yearlyEarnings.toLocaleString('en-US', { maximumFractionDigits: 0 })}
              </p>
            </div>
          </div>
        </div>
        
        {/* CTA */}
        {onCTA && (
          <Button 
            onClick={onCTA} 
            size="lg" 
            className="w-full text-lg py-6"
          >
            Start Your Application
          </Button>
        )}
        
        <p className="text-xs text-center text-muted-foreground">
          * Earnings are estimates based on average session rates and availability
        </p>
      </CardContent>
    </Card>
  );
};

export default EarningsCalculator;
